import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiError } from '../../abstract/api-error';
import { AgremoAnalysisService } from '../../core/data-services/agremo-analysis.service';
import { AgremoAnalysis } from '../../core/data-models/agremo-analysis.model';
import { AgremoAnalysisFilters } from '../../core/request-models/agremo-analysis-filters.model';
import { AgremoAnalysisStatus } from '../../core/data-models/agremo-analysis-status.model';

/**
 * Agremo Analyses Component (Global)
 *
 * List of ALL analyses across all organizations.
 * Supports filtering by organization, status, and date range.
 * Admins can retry failed/completed downloads.
 */
@Component({
  selector: 'app-agremo-analyses',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './agremo-analyses.component.html',
  styleUrl: './agremo-analyses.component.scss'
})
export class AgremoAnalysesComponent implements OnInit {
  analyses: AgremoAnalysis[] = [];
  loading = true;
  errorMessage = '';
  successMessage = '';
  retryingId: string | null = null;

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalAnalyses = 0;
  totalPages = 0;

  // Filters
  filterOrganizationId = '';
  filterStatus: AgremoAnalysisStatus | 'all' = 'all';
  filterStartDate = '';
  filterEndDate = '';

  // Status options
  statusOptions: { value: AgremoAnalysisStatus | 'all'; label: string }[] = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'queued', label: 'Queued' },
    { value: 'processing', label: 'Processing' },
    { value: 'downloading', label: 'Downloading' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' }
  ];

  constructor(
    private agremoService: AgremoAnalysisService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadAnalyses();
  }

  loadAnalyses(): void {
    this.loading = true;
    this.errorMessage = '';

    const filters: AgremoAnalysisFilters = {
      page: this.currentPage,
      limit: this.pageSize
    };

    if (this.filterOrganizationId.trim()) {
      filters.organizationId = this.filterOrganizationId.trim();
    }

    if (this.filterStatus !== 'all') {
      filters.status = this.filterStatus;
    }

    if (this.filterStartDate) {
      filters.startDate = this.filterStartDate;
    }

    if (this.filterEndDate) {
      filters.endDate = this.filterEndDate;
    }

    this.agremoService.listAnalyses(filters).subscribe({
      next: (page) => {
        this.analyses = page.items;
        this.totalAnalyses = page.paging.totalItems;
        this.totalPages = page.paging.totalPages;
        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load analyses:', error);
        this.errorMessage = ApiError.fromHttpError(error).message;
        this.loading = false;
      }
    });
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadAnalyses();
  }

  clearFilters(): void {
    this.filterOrganizationId = '';
    this.filterStatus = 'all';
    this.filterStartDate = '';
    this.filterEndDate = '';
    this.currentPage = 1;
    this.loadAnalyses();
  }

  hasActiveFilters(): boolean {
    return (
      this.filterOrganizationId !== '' ||
      this.filterStatus !== 'all' ||
      this.filterStartDate !== '' ||
      this.filterEndDate !== ''
    );
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadAnalyses();
    }
  }

  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  getStatusBadgeClass(status: string | null | undefined): string {
    switch (status) {
      case 'completed': return 'bg-success';
      case 'processing': return 'bg-info';
      case 'downloading': return 'bg-info text-dark';
      case 'queued': return 'bg-primary';
      case 'pending': return 'bg-warning text-dark';
      case 'failed': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  getStatusLabel(status: string | null | undefined): string {
    switch (status) {
      case 'completed': return 'Completed';
      case 'processing': return 'Processing';
      case 'downloading': return 'Downloading Results';
      case 'queued': return 'Queued';
      case 'pending': return 'Pending';
      case 'failed': return 'Failed';
      default: return status ?? '-';
    }
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  canRetryDownload(analysis: AgremoAnalysis): boolean {
    return !!analysis.agremoAnalysisId &&
      (analysis.status === 'completed' || analysis.status === 'failed' || analysis.status === 'downloading');
  }

  retryDownload(analysis: AgremoAnalysis): void {
    if (this.retryingId) return;

    this.retryingId = analysis.id;
    this.errorMessage = '';
    this.successMessage = '';

    this.agremoService.retryDownload(analysis.id).subscribe({
      next: () => {
        this.successMessage = 'Download retry queued successfully';
        this.retryingId = null;
        this.loadAnalyses();
      },
      error: (error) => {
        console.error('Failed to retry download:', error);
        this.errorMessage = ApiError.fromHttpError(error).message;
        this.retryingId = null;
      }
    });
  }

  viewAnalysis(analysis: AgremoAnalysis): void {
    this.router.navigate(['/agremo-analyses', analysis.id]);
  }

  readonly Math = Math;
}
